import { useCallback, useMemo, useState } from "react";
import AppShell from "@/pages/home/components/AppShell";
import { useAuth } from "@/context/AuthContext";
import * as api from "@/lib/api";
import type { BirthdayCouponInfo } from "@/lib/types";

function formatExpiresJa(iso: string): string {
  try {
    const d = new Date(iso);
    return new Intl.DateTimeFormat("ja-JP", {
      timeZone: "Asia/Tokyo",
      year: "numeric",
      month: "long",
      day: "numeric",
    }).format(d);
  } catch {
    return iso;
  }
}

function couponStatus(c: BirthdayCouponInfo): "usable" | "used" | "expired" {
  if (c.used) return "used";
  const exp = new Date(c.expiresAt).getTime();
  if (!Number.isFinite(exp) || exp <= Date.now()) return "expired";
  return "usable";
}

export default function CouponsPage() {
  const { user, loading, firebaseUser, getIdToken, refreshUser } = useAuth();
  const [busy, setBusy] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const c = user?.birthdayCoupon ?? null;
  const status = useMemo(() => (c ? couponStatus(c) : null), [c]);

  const onMarkUsed = useCallback(async () => {
    setLocalError(null);
    const token = await getIdToken(true);
    if (!token) {
      setLocalError("ログイン情報を取得できませんでした");
      return;
    }
    setBusy(true);
    try {
      await api.markBirthdayCouponUsed(token);
      await refreshUser();
    } catch (e) {
      setLocalError(e instanceof Error ? e.message : "更新に失敗しました");
    } finally {
      setBusy(false);
    }
  }, [getIdToken, refreshUser]);

  if (loading && !user) {
    return (
      <AppShell>
        <div className="px-4 py-16 text-center text-sm" style={{ color: "rgba(244,229,195,0.5)" }}>
          読み込み中…
        </div>
      </AppShell>
    );
  }

  if (!user || !firebaseUser) {
    return (
      <AppShell>
        <div className="px-4 py-16 text-center text-sm" style={{ color: "#ff8080" }}>
          会員情報を表示できません。
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="px-4 py-5 space-y-5 max-w-lg mx-auto">
        <div className="flex items-center gap-3">
          <div className="h-px flex-1" style={{ background: "rgba(212,175,55,0.2)" }} />
          <span className="text-xs tracking-widest" style={{ color: "rgba(212,175,55,0.5)", fontFamily: "Montserrat, sans-serif" }}>
            COUPONS
          </span>
          <div className="h-px flex-1" style={{ background: "rgba(212,175,55,0.2)" }} />
        </div>

        {!c ? (
          <p className="text-sm text-center leading-relaxed" style={{ color: "rgba(244,229,195,0.55)" }}>
            現在お使いいただけるクーポンはありません。
            <br />
            誕生月にはアプリへ誕生月クーポンが届きます（プッシュ通知でお知らせします）。
          </p>
        ) : (
          <div
            className="rounded-2xl p-5 space-y-4"
            style={{
              background: "linear-gradient(135deg, #1a2f2f 0%, #0d1f1f 100%)",
              border: "1px solid rgba(212,175,55,0.3)",
            }}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-bold tracking-wider" style={{ color: "#d4af37", fontFamily: "Montserrat, sans-serif" }}>
                  {c.kind === "birthday" ? "BIRTHDAY" : c.kind.toUpperCase()}
                </p>
                <h2 className="text-lg font-bold mt-1" style={{ color: "#f4e5c3" }}>
                  {c.title}
                </h2>
              </div>
              <span
                className="text-[10px] font-bold px-2 py-1 rounded-full shrink-0"
                style={{
                  background:
                    status === "usable"
                      ? "rgba(120,200,140,0.25)"
                      : status === "used"
                        ? "rgba(180,180,190,0.2)"
                        : "rgba(255,128,128,0.2)",
                  color:
                    status === "usable"
                      ? "#b8e6c0"
                      : status === "used"
                        ? "rgba(244,229,195,0.7)"
                        : "#ffb4b4",
                  border: "1px solid rgba(212,175,55,0.25)",
                }}
              >
                {status === "usable" ? "利用可能" : status === "used" ? "使用済" : "期限切れ"}
              </span>
            </div>
            <p className="text-xs leading-relaxed" style={{ color: "rgba(244,229,195,0.65)" }}>
              有効期限（店舗の営業日・会計まで）:{" "}
              <span className="font-medium" style={{ color: "#f4e5c3" }}>
                {formatExpiresJa(c.expiresAt)} まで
              </span>
            </p>
            <p className="text-[11px] leading-relaxed" style={{ color: "rgba(244,229,195,0.45)" }}>
              会計時にスタッフへ画面をご提示ください。利用済みにしたあと再度表示はできません。
            </p>
            {status === "usable" && (
              <button
                type="button"
                disabled={busy}
                onClick={() => void onMarkUsed()}
                className="w-full py-3 rounded-xl text-sm font-bold disabled:opacity-50"
                style={{
                  background: "rgba(212,175,55,0.15)",
                  border: "1px solid rgba(212,175,55,0.4)",
                  color: "#f4e5c3",
                }}
              >
                {busy ? "処理中…" : "会計で利用した（使用済みにする）"}
              </button>
            )}
            {localError && (
              <p className="text-xs text-center" style={{ color: "#ff8080" }}>
                {localError}
              </p>
            )}
          </div>
        )}
      </div>
    </AppShell>
  );
}
